import React, { useState, useEffect } from 'react';
import Input from '../components/common/Input';
import { useNavigate, useLocation } from 'react-router-dom';
import { ResendOtp, VerifyOtp } from '../services/accountService';

const VerifyOtpForgot = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [formData, setFormData] = useState({
        otp: ''
    });
    const [timeLeft, setTimeLeft] = useState(300);
    const [isExpired, setIsExpired] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    // Get email from multiple sources
    useEffect(() => {
        const locationEmail = location.state?.email;
        const storedEmail = localStorage.getItem('otpEmail') ||
            localStorage.getItem('forgotPasswordEmail') ||
            localStorage.getItem('userEmail');
        const defaultEmail = 'shivangi.gupta@aisinfo.com';

        const email = locationEmail || storedEmail || defaultEmail;
        setUserEmail(email);

        if (email && email !== defaultEmail) {
            localStorage.setItem('otpEmail', email);
        }

        // console.log('OTP Verification for email:', email);
    }, [location]);

    // Countdown timer
    useEffect(() => {
        if (timeLeft <= 0) {
            setIsExpired(true);
            return;
        }

        const timer = setTimeout(() => {
            setTimeLeft(timeLeft - 1);
        }, 1000);

        return () => clearTimeout(timer);
    }, [timeLeft]);

    // Format time
    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    // Timer color
    const getTimerColor = () => {
        if (isExpired) return 'text-danger';
        if (timeLeft <= 60) return 'text-warning';
        return 'text-primary';
    };

    // Handle input change
    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'otp') {
            const digitsOnly = value.replace(/\D/g, '');
            if (digitsOnly.length <= 6) {
                setFormData(prev => ({ ...prev, [name]: digitsOnly }));
                setErrorMessage('');
            }
        }
    };


    const handleOtpBlur = (e) => {
        const otpValue = e.target.value.trim();

        if (!otpValue) {
            setErrorMessage('Please enter OTP.');
        } else if (otpValue.length !== 6) {
            setErrorMessage('OTP must be 6 digits.');
        } else {
            setErrorMessage('');
        }
    };


    // ACTUAL OTP VERIFICATION WITH API
    const handleSubmit = async (e) => {
        e.preventDefault();


        if (!formData.otp || formData.otp.trim() === '') {
            setErrorMessage('Please enter OTP.');
            return;
        }

        if (isExpired) {
            setErrorMessage('OTP has expired. Please request a new one.');
            return;
        }

        if (formData.otp.length !== 6) {
            setErrorMessage('Please enter a valid 6-digit OTP.');
            return;
        }

        setIsLoading(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            // Use VerifyOtp API
            const response = await VerifyOtp(userEmail, formData.otp);

            // console.log('OTP Verification response:', response);

            // Check if response indicates success (200 status)
            if (response) {
                // Show success message
                setSuccessMessage(response.message || 'OTP verified successfully!');

                // Store verification token if provided
                if (response.token) {
                    localStorage.setItem('otpVerificationToken', response.token);
                }

                // Store email for reset password page
                localStorage.setItem('resetPasswordEmail', userEmail);

                // IMPORTANT: Store BOTH email AND OTP in localStorage
                const resetData = {
                    email: userEmail,
                    otp: formData.otp, // YEH LINE IMPORTANT HAI - OTP store karo
                    timestamp: new Date().getTime(),
                    token: response.token || null
                };

                localStorage.setItem('resetPasswordData', JSON.stringify(resetData));

                // Navigate to reset password page after 2 seconds
                setTimeout(() => {
                    navigate('/reset-password', {
                        state: {
                            email: userEmail,
                            token: response.token
                        }
                    });
                }, 2000);
            }

        } catch (error) {
            console.error('OTP verification failed:', error);

          
            if (error.response?.data?.errors?.message) {
                // Array format mein aaya hai ["Invalid OTP."]
                const errorMessages = error.response.data.errors.message;
                setErrorMessage(Array.isArray(errorMessages) ? errorMessages[0] : errorMessages);
            }
            else if (error.response?.data?.message) {
                // Direct message field se
                setErrorMessage(error.response.data.message);
            }
            else if (error.message.includes('Invalid') || error.message.includes('incorrect')) {
                setErrorMessage('Invalid OTP. Please check and try again.');
            }
            else if (error.message.includes('expired')) {
                setErrorMessage('OTP has expired. Please request a new one.');
                setIsExpired(true);
            }
            else if (error.message.includes('429')) {
                setErrorMessage('Too many attempts. Please wait before trying again.');
            }
            else {
                setErrorMessage(error.message || 'OTP verification failed. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    // ACTUAL RESEND OTP WITH API
    const handleResendOtp = async () => {
        if (resendLoading || !userEmail) return;

        setResendLoading(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const response = await ResendOtp(userEmail);

      

            setTimeLeft(300);
            setIsExpired(false);
            setFormData({ otp: '' });

            // Show success message
            setSuccessMessage(response.message || 'A new OTP has been sent to your email.');

            // Clear success message after 3 seconds
            setTimeout(() => {
                setSuccessMessage('');
            }, 3000);

        } catch (error) {
            console.error('Failed to resend OTP:', error);

            if (error.message.includes('429')) {
                setErrorMessage('Too many resend requests. Please wait.');
            } else if (error.message.includes('404')) {
                setErrorMessage('Email not found. Please check your email address.');
            } else {
                setErrorMessage(error.message || 'Failed to resend OTP.');
            }
        } finally {
            setResendLoading(false);
        }
    };

    const handleBackToLogin = () => {
        localStorage.removeItem('otpEmail');
        localStorage.removeItem('forgotPasswordEmail');
        localStorage.removeItem('resetPasswordEmail');
        navigate('/');
    };

    return (
        <div className="login-page">
            {/* Left Section - Welcome Content */}
            <div className="welcome-section">
                <div className="welcome-content">
                    <h1 className="welcome-title">Welcome to OpenBook</h1>
                    {/* <p className="purpose-text">Purpose</p> */}
                </div>
            </div>

            {/* Right Section - Login Form */}
            <div className="login-section">
                <div className="login-form-wrapper">
                    <div className='rockwell_logo'>
                        <img src='../../assets/Rockwell-Logo.jpg' alt='rockwell_logo' />
                    </div>

                    <div className="form-title-section">
                        <h4 className="form-title">Verify OTP</h4>

                        <p className={`form-subtitle ${isExpired ? 'text-danger' : ''}`}>
                            {isExpired ? (
                                'OTP has expired. Please request a new one.'
                            ) : (
                                <>
                                    Please enter the 6-digit code from your email.<br />
                                    {/* <strong>{userEmail}</strong>. <br/> */}
                                    Expires in{' '}
                                    <span className={getTimerColor()}>
                                        {formatTime(timeLeft)}
                                    </span>
                                    .
                                </>
                            )}
                        </p>
                    </div>

                    {/* Success Message Display */}
                    {successMessage && (
                        <div className="alert alert-success" role="alert">
                            <div className="d-flex align-items-center">
                                <i className="bi bi-check-circle-fill me-2"></i>
                                <span>{successMessage}</span>
                                {successMessage.includes('successfully') && (
                                    <div className="ms-2">
                                        <div className="spinner-border spinner-border-sm text-success" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <span className="ms-1">Redirecting...</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Error Message Display */}
                    {errorMessage && (
                        <div className="api-error-message" role="alert">
                            <i className="bi bi-exclamation-circle-fill me-2"></i>
                            {errorMessage}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="login-form">
                        <Input
                            label={<span>OTP <span style={{ color: 'red' }}>*</span></span>}
                            type="text"
                            inputMode="numeric"
                            pattern="\d*"
                            name="otp"
                            value={formData.otp}
                            onBlur={handleOtpBlur} 
                            onChange={handleChange}
                            required
                            placeholder="Enter 6-digit OTP"
                            maxLength="6"
                            disabled={isExpired || isLoading || successMessage.includes('successfully')}
                            className={errorMessage ? 'is-invalid' : ''}
                        />

                        {/* {errorMessage && errorMessage === 'Please enter OTP.' && (
                            <div className="invalid-feedback d-block">
                                <i className="bi bi-exclamation-circle me-1"></i>
                                Please enter OTP.
                            </div>
                        )} */}

                        <button
                            type="submit"
                            className={`login-btn ${isLoading ? 'loading' : ''}`}
                            disabled={isExpired || isLoading || formData.otp.length !== 6 || successMessage.includes('successfully')}
                        >
                            {isLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Verifying...
                                </>
                            ) : 'Verify OTP'}
                        </button>
                    </form>

                    {/* Resend OTP Section */}
                    <div className="resend-section">
                        <p className="resend-text">
                            {isExpired ? (
                                <>
                                    Didn't receive the OTP or it expired?{' '}
                                    <button
                                        className={`btn btn-link p-0 text-primary ${resendLoading || successMessage.includes('successfully') ? 'disabled' : ''}`}
                                        onClick={handleResendOtp}
                                        disabled={resendLoading || successMessage.includes('successfully')}
                                    >
                                        {resendLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                Sending...
                                            </>
                                        ) : 'Resend OTP'}
                                    </button>
                                </>
                            ) : (
                                <>
                                    Didn't receive the OTP?{' '}
                                    <button
                                        className={`btn btn-link p-0 text-primary ${resendLoading || successMessage.includes('successfully') ? 'disabled' : ''}`}
                                        onClick={handleResendOtp}
                                        disabled={resendLoading || successMessage.includes('successfully')}
                                    >
                                        {resendLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                Sending...
                                            </>
                                        ) : 'Resend OTP'}
                                    </button>
                                </>
                            )}
                        </p>
                    </div>

                    <div className="register-section">
                        <p className="register-text">
                            <button className="btn btn-link p-0 text-secondary" onClick={handleBackToLogin}>
                                ← Back to Login
                            </button>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VerifyOtpForgot;